import type { LucideIcon } from "lucide-react-native";
import { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import MobileSheetChrome from "@/components/molecules/MobileSheetChrome";

/** A single row of the mobile action menu, with its press already wired. */
export interface MobileActionMenuViewItem {
  /** Optional leading lucide icon. */
  readonly icon?: LucideIcon;
  /** Row label. */
  readonly label: string;
  /** Optional secondary line below the label. */
  readonly description?: string;
  /** Already-wrapped press handler (closes the menu, then acts). */
  readonly onPress: () => void;
  /** Style the row as a destructive action. */
  readonly destructive?: boolean;
  /** Render the row inert and dimmed. */
  readonly disabled?: boolean;
  /** Row testID. */
  readonly testID?: string;
}

/** Props for MobileActionMenuView */
interface MobileActionMenuViewProps {
  /** Whether the menu sheet is open. */
  readonly isOpen: boolean;
  /** Unified dismissal callback (chrome owns the paths). */
  readonly onClose: () => void;
  /** Optional sheet title. */
  readonly title?: string;
  /** Optional secondary line pinned under the title. */
  readonly subtitle?: string;
  /** Menu rows with press handlers pre-wired by the Container. */
  readonly items: readonly MobileActionMenuViewItem[];
  /** Root testID; rows derive theirs when not explicitly set. */
  readonly testID?: string;
}

const labelClass = (item: MobileActionMenuViewItem) =>
  item.destructive ? "text-status-error" : "text-content-primary";

const iconClass = (item: MobileActionMenuViewItem) =>
  item.destructive ? "text-status-error" : "text-content-secondary";

/**
 * Canonical mobile action menu: a MobileSheetChrome sheet whose body is a
 * list of tappable rows (icon + label + optional description). Replaces the
 * per-feature Actionsheet-as-menu pattern so menus inherit the chrome's
 * insets and dismissal paths.
 * @param props - Component props
 * @param props.isOpen - Whether the menu sheet is open
 * @param props.onClose - Unified dismissal callback
 * @param props.title - Optional sheet title
 * @param props.subtitle - Optional secondary line pinned under the title
 * @param props.items - Menu rows with press handlers pre-wired
 * @param props.testID - Root testID; rows derive theirs when not set
 */
const MobileActionMenuView = ({
  isOpen,
  onClose,
  title,
  subtitle,
  items,
  testID,
}: MobileActionMenuViewProps) => (
  <MobileSheetChrome
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    headerContent={
      subtitle ? (
        <Text variant="caption" className="px-4 text-content-muted">
          {subtitle}
        </Text>
      ) : undefined
    }
  >
    <Stack className="pb-2">
      {items.map((item, index) => (
        <Pressable
          key={item.testID ?? `${item.label}-${index}`}
          onPress={item.onPress}
          disabled={item.disabled}
          className={
            item.disabled
              ? "flex-row items-center gap-3 px-4 py-3 opacity-40"
              : "flex-row items-center gap-3 px-4 py-3"
          }
          accessibilityRole="menuitem"
          accessibilityLabel={item.label}
          accessibilityState={{ disabled: item.disabled === true }}
          testID={
            item.testID ?? (testID ? `${testID}-item-${index}` : undefined)
          }
        >
          {item.icon ? (
            <Icon as={item.icon} size="md" className={iconClass(item)} />
          ) : null}
          <Stack className="flex-1">
            <Text variant="body" className={labelClass(item)}>
              {item.label}
            </Text>
            {item.description ? (
              <Text variant="caption" className="text-content-muted">
                {item.description}
              </Text>
            ) : null}
          </Stack>
        </Pressable>
      ))}
    </Stack>
  </MobileSheetChrome>
);

MobileActionMenuView.displayName = "MobileActionMenuView";

export default memo(MobileActionMenuView);
